// sign-v2 — signing the inspection agreement remotely (Owner decision list 2026-10-19 items 4 and 45): the
// inspector sends the client a one-time link; the client opens it on their own phone or computer WITHOUT an
// account, reads the whole agreement (the same document the PDF prints), ticks the consent to electronic records
// and signatures (required) and the AI consent (voluntary), types their name and signs. The inspector's device
// then takes the client's part back and the inspector signs on their own device. The server holds the request
// only until the inspector has taken the signature (or 14 days), then deletes it.
//
// Shared by the app (src/backend/remoteSignClient.ts), the public signing page (src/features/remoteSign) and the
// function (supabase/functions/sign-v2). Deno-free.

export const REMOTE_SIGN_PROTOCOL_VERSION = 1;
export const REMOTE_SIGN_FUNCTION_PATH = '/functions/v1/sign-v2';
export const REMOTE_SIGN_TTL_DAYS = 14;
// A signed request the inspector never collected is deleted after this many days.
export const REMOTE_SIGN_UNCOLLECTED_DAYS = 30;
export const REMOTE_SIGN_MAX_DOCUMENT_CHARS = 800_000;
// The page the link opens (the web version): `${base}/sign/${token}`.
export const REMOTE_SIGN_PATH_PREFIX = '/sign/';

export type RemoteSignLocale = 'zh-CN' | 'en-US';

// The facts printed on the signing page above the document (the same five as the in-person signing sheet),
// and a fingerprint of everything the client's signature covers: if the inspector changes any of it after
// sending the link, the signature taken back no longer matches and is refused.
export interface RemoteSignFacts {
  property: string;
  client: string;
  inspector: string;
  inspectionDate: string;
  fee: string;
  textVersion: string;
  fingerprint: string;
}

export interface RemoteSignStrokes {
  width: number;
  height: number;
  strokes: number[][];
}

// What the client gives on the page.
export interface RemoteSignSubmission {
  name: string;
  signature: RemoteSignStrokes;
  accepted: true;
  esignConsent: true;
  // When the client ticked each box, on their device's clock (the server also keeps when it received them).
  esignConsentAt: number;
  aiConsent: boolean;
  aiConsentAt: number | null;
  // Agreement copy of 2026-10-28: when the client accepted the limitation of liability and claims terms. Optional
  // on the wire: a page or a server from before it sends / keeps none, and the agreement then stays open.
  liabilityAckAt?: number | null;
}

// What the inspector's device takes back.
export interface RemoteSignResult extends RemoteSignSubmission {
  // Server time the signature arrived — the time the agreement records for the client's signature.
  signedAt: number;
  // Evidence of the electronic signature (kept on the agreement record, printed nowhere).
  ip: string | null;
  userAgent: string | null;
}

export type RemoteSignState = 'waiting' | 'signed' | 'expired';

export type RemoteSignRequest =
  | { action: 'create'; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; inspectionId: string; documentHtml: string; facts: RemoteSignFacts; locale: RemoteSignLocale }
  | { action: 'status'; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; requestId: string }
  | { action: 'discard'; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; requestId: string }
  | { action: 'open'; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; token: string }
  | { action: 'sign'; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; token: string; submission: RemoteSignSubmission };

export type RemoteSignErrorCode = 'unauthenticated' | 'badRequest' | 'notFound' | 'expired' | 'alreadySigned' | 'notConfigured' | 'internalError';

export type RemoteSignResponse =
  | { ok: true; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; action: 'create'; requestId: string; token: string; expiresAt: number }
  | { ok: true; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; action: 'status'; state: RemoteSignState; result: RemoteSignResult | null; expiresAt: number }
  | { ok: true; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; action: 'discard' }
  | { ok: true; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; action: 'open'; state: RemoteSignState; documentHtml: string; facts: RemoteSignFacts; locale: RemoteSignLocale; expiresAt: number }
  | { ok: true; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; action: 'sign'; signedAt: number }
  | { ok: false; protocolVersion: typeof REMOTE_SIGN_PROTOCOL_VERSION; code: RemoteSignErrorCode; message: string };

type Rec = Record<string, unknown>;
const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, max: number): string | null => (typeof v === 'string' && v.length <= max ? v : null);
const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

// A token is 32 random bytes, base64url (43 characters).
export function isRemoteSignToken(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{40,64}$/.test(value);
}

export function remoteSignErr(code: RemoteSignErrorCode, message: string): RemoteSignResponse {
  return { ok: false, protocolVersion: REMOTE_SIGN_PROTOCOL_VERSION, code, message };
}

function checkFacts(raw: unknown): RemoteSignFacts | null {
  if (!isRec(raw)) return null;
  const keys = ['property', 'client', 'inspector', 'inspectionDate', 'fee', 'textVersion', 'fingerprint'] as const;
  const out: Partial<RemoteSignFacts> = {};
  for (const key of keys) {
    const value = str(raw[key], 400);
    if (value === null) return null;
    out[key] = value;
  }
  return out as RemoteSignFacts;
}

// The signature must be a real mark: the same minimum ink as the device (src/domain/signature.ts, 24 px), and
// bounded so a page cannot post megabytes of points.
export function checkStrokes(raw: unknown): RemoteSignStrokes | null {
  if (!isRec(raw)) return null;
  const width = finite(raw.width);
  const height = finite(raw.height);
  if (width === null || height === null || width <= 0 || height <= 0 || width > 4000 || height > 4000 || !Array.isArray(raw.strokes) || raw.strokes.length > 300) return null;
  let points = 0;
  let ink = 0;
  const strokes: number[][] = [];
  for (const stroke of raw.strokes) {
    if (!Array.isArray(stroke) || stroke.length % 2 !== 0 || !stroke.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
    points += stroke.length / 2;
    if (points > 20_000) return null;
    for (let i = 2; i < stroke.length; i += 2) ink += Math.hypot(stroke[i] - stroke[i - 2], stroke[i + 1] - stroke[i - 1]);
    strokes.push(stroke.map((n) => Math.round(n * 10) / 10));
  }
  return ink >= 24 ? { width, height, strokes } : null;
}

export function checkSubmission(raw: unknown): RemoteSignSubmission | null {
  if (!isRec(raw)) return null;
  const name = str(raw.name, 200)?.trim() ?? '';
  const signature = checkStrokes(raw.signature);
  const esignConsentAt = finite(raw.esignConsentAt);
  const aiConsentAt = raw.aiConsentAt === null ? null : finite(raw.aiConsentAt);
  if (name.length === 0 || !signature || raw.accepted !== true || raw.esignConsent !== true || esignConsentAt === null || typeof raw.aiConsent !== 'boolean') return null;
  if (raw.aiConsent && aiConsentAt === null) return null;
  const liabilityAckAt = raw.liabilityAckAt === undefined || raw.liabilityAckAt === null ? null : finite(raw.liabilityAckAt);
  return { name, signature, accepted: true, esignConsent: true, esignConsentAt, aiConsent: raw.aiConsent, aiConsentAt: raw.aiConsent ? aiConsentAt : null, liabilityAckAt };
}

export function checkRemoteSignRequest(raw: unknown): { ok: true; request: RemoteSignRequest } | { ok: false; message: string } {
  if (!isRec(raw)) return { ok: false, message: 'request body is not an object' };
  if (raw.protocolVersion !== REMOTE_SIGN_PROTOCOL_VERSION) return { ok: false, message: 'unsupported protocolVersion' };
  const v = REMOTE_SIGN_PROTOCOL_VERSION;
  switch (raw.action) {
    case 'create': {
      const inspectionId = str(raw.inspectionId, 120);
      const documentHtml = str(raw.documentHtml, REMOTE_SIGN_MAX_DOCUMENT_CHARS);
      const facts = checkFacts(raw.facts);
      const locale = raw.locale === 'zh-CN' || raw.locale === 'en-US' ? raw.locale : null;
      if (!inspectionId || !documentHtml || !facts || !locale) return { ok: false, message: 'create needs inspectionId, documentHtml, facts and locale' };
      return { ok: true, request: { action: 'create', protocolVersion: v, inspectionId, documentHtml, facts, locale } };
    }
    case 'status':
    case 'discard': {
      const requestId = str(raw.requestId, 64);
      if (!requestId || !/^[0-9a-f-]{36}$/i.test(requestId)) return { ok: false, message: 'requestId missing' };
      return { ok: true, request: { action: raw.action, protocolVersion: v, requestId } };
    }
    case 'open':
      return isRemoteSignToken(raw.token) ? { ok: true, request: { action: 'open', protocolVersion: v, token: raw.token } } : { ok: false, message: 'token missing' };
    case 'sign': {
      if (!isRemoteSignToken(raw.token)) return { ok: false, message: 'token missing' };
      const submission = checkSubmission(raw.submission);
      return submission ? { ok: true, request: { action: 'sign', protocolVersion: v, token: raw.token, submission } } : { ok: false, message: 'the signature is incomplete' };
    }
    default:
      return { ok: false, message: 'unknown action' };
  }
}

function checkResult(raw: unknown): RemoteSignResult | null {
  if (!isRec(raw)) return null;
  const submission = checkSubmission(raw);
  const signedAt = finite(raw.signedAt);
  if (!submission || signedAt === null) return null;
  return { ...submission, signedAt, ip: str(raw.ip, 100), userAgent: str(raw.userAgent, 400) };
}

export function checkRemoteSignResponse(raw: unknown): RemoteSignResponse | null {
  if (!isRec(raw) || raw.protocolVersion !== REMOTE_SIGN_PROTOCOL_VERSION) return null;
  const v = REMOTE_SIGN_PROTOCOL_VERSION;
  if (raw.ok === false) {
    const codes: RemoteSignErrorCode[] = ['unauthenticated', 'badRequest', 'notFound', 'expired', 'alreadySigned', 'notConfigured', 'internalError'];
    return codes.includes(raw.code as RemoteSignErrorCode) ? { ok: false, protocolVersion: v, code: raw.code as RemoteSignErrorCode, message: typeof raw.message === 'string' ? raw.message : '' } : null;
  }
  if (raw.ok !== true) return null;
  const state = raw.state === 'waiting' || raw.state === 'signed' || raw.state === 'expired' ? raw.state : null;
  const expiresAt = finite(raw.expiresAt);
  switch (raw.action) {
    case 'create':
      return typeof raw.requestId === 'string' && isRemoteSignToken(raw.token) && expiresAt !== null ? { ok: true, protocolVersion: v, action: 'create', requestId: raw.requestId, token: raw.token, expiresAt } : null;
    case 'status': {
      if (!state || expiresAt === null) return null;
      const result = raw.result === null || raw.result === undefined ? null : checkResult(raw.result);
      if (state === 'signed' && !result) return null;
      return { ok: true, protocolVersion: v, action: 'status', state, result, expiresAt };
    }
    case 'discard':
      return { ok: true, protocolVersion: v, action: 'discard' };
    case 'open': {
      const facts = checkFacts(raw.facts);
      const documentHtml = str(raw.documentHtml, REMOTE_SIGN_MAX_DOCUMENT_CHARS);
      const locale = raw.locale === 'zh-CN' || raw.locale === 'en-US' ? raw.locale : null;
      return state && facts && documentHtml !== null && locale && expiresAt !== null ? { ok: true, protocolVersion: v, action: 'open', state, documentHtml, facts, locale, expiresAt } : null;
    }
    case 'sign': {
      const signedAt = finite(raw.signedAt);
      return signedAt !== null ? { ok: true, protocolVersion: v, action: 'sign', signedAt } : null;
    }
    default:
      return null;
  }
}

export function remoteSignUrl(base: string, token: string): string {
  return `${base.trim().replace(/\/$/, '')}${REMOTE_SIGN_PATH_PREFIX}${token}`;
}
